export const DOT_TYPES = [
    'square',
    'dots',
    'rounded',
    'classy',
    'classy-rounded',
    'extra-rounded',
];

export const CORNER_SQUARE_TYPES = [
    'square',
    'rounded',
    'dot',
    'classy-rounded',
    'circle',
];

export const CORNER_DOT_TYPES = [
    'square',
    'dot',
    'dots',
    'rounded',
    'classy',
];

export const ECC_LEVELS = ['L', 'M', 'Q', 'H'];

export const DEFAULT_QR_DESIGN = {
    qrOptions: {
        errorCorrectionLevel: 'M',
    },
    backgroundOptions: {
        color: '#ffffff',
    },
    dotsOptions: {
        color: '#0f172a',
        type: 'square',
        gradient: null,
    },
    cornersSquareOptions: {
        type: 'rounded',
    },
    cornersDotOptions: {
        type: 'rounded',
    },
    logoFile: null,
    logoFileName: null,
    logoPreview: null,
    image: null,
    imageOptions: {
        imageSize: 0.2,
        margin: 2,
        hideBackgroundDots: true,
    },
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toColorStop = (stop, fallbackOffset, fallbackColor) => ({
    offset: typeof stop?.offset === 'number' ? stop.offset : fallbackOffset,
    color: typeof stop?.color === 'string' ? stop.color : fallbackColor,
});

export const ensureQrDesign = (design) => {
    const base = design || {};
    const baseDots = base.dotsOptions || {};
    const gradientStops = baseDots.gradient?.colorStops;

    return {
        qrOptions: {
            ...DEFAULT_QR_DESIGN.qrOptions,
            ...(base.qrOptions || {}),
        },
        backgroundOptions: {
            ...DEFAULT_QR_DESIGN.backgroundOptions,
            ...(base.backgroundOptions || {}),
        },
        dotsOptions: {
            ...DEFAULT_QR_DESIGN.dotsOptions,
            ...baseDots,
            gradient: Array.isArray(gradientStops) && gradientStops.length >= 2
                ? {
                    colorStops: [
                        toColorStop(gradientStops[0], 0, '#ff0066'),
                        toColorStop(gradientStops[gradientStops.length - 1], 1, '#0066ff'),
                    ],
                }
                : null,
        },
        cornersSquareOptions: {
            ...DEFAULT_QR_DESIGN.cornersSquareOptions,
            ...(base.cornersSquareOptions || {}),
        },
        cornersDotOptions: {
            ...DEFAULT_QR_DESIGN.cornersDotOptions,
            ...(base.cornersDotOptions || {}),
        },
        logoFile: base.logoFile || null,
        logoFileName: typeof base.logoFileName === 'string' ? base.logoFileName : null,
        logoPreview: typeof base.logoPreview === 'string' ? base.logoPreview : null,
        image: typeof base.image === 'string' && base.image.trim() ? base.image.trim() : null,
        imageOptions: {
            ...DEFAULT_QR_DESIGN.imageOptions,
            ...(base.imageOptions || {}),
        },
    };
};

export const normalizeQrDesign = (design) => {
    const merged = ensureQrDesign(design);

    const dotsType = DOT_TYPES.includes(merged.dotsOptions.type)
        ? merged.dotsOptions.type
        : DEFAULT_QR_DESIGN.dotsOptions.type;

    const cornerSquareType = CORNER_SQUARE_TYPES.includes(merged.cornersSquareOptions.type)
        ? merged.cornersSquareOptions.type
        : DEFAULT_QR_DESIGN.cornersSquareOptions.type;

    const cornerDotType = CORNER_DOT_TYPES.includes(merged.cornersDotOptions.type)
        ? merged.cornersDotOptions.type
        : DEFAULT_QR_DESIGN.cornersDotOptions.type;

    const ecc = ECC_LEVELS.includes(merged.qrOptions.errorCorrectionLevel)
        ? merged.qrOptions.errorCorrectionLevel
        : DEFAULT_QR_DESIGN.qrOptions.errorCorrectionLevel;

    const gradient = merged.dotsOptions.gradient?.colorStops;
    const hasGradient = Array.isArray(gradient) && gradient.length >= 2;

    return {
        ...merged,
        qrOptions: {
            errorCorrectionLevel: ecc,
        },
        dotsOptions: {
            ...merged.dotsOptions,
            type: dotsType,
            gradient: hasGradient
                ? {
                    colorStops: [
                        toColorStop(gradient[0], 0, '#ff0066'),
                        toColorStop(gradient[gradient.length - 1], 1, '#0066ff'),
                    ],
                }
                : null,
        },
        cornersSquareOptions: {
            type: cornerSquareType,
        },
        cornersDotOptions: {
            type: cornerDotType,
        },
        imageOptions: {
            imageSize: clamp(Number(merged.imageOptions.imageSize || 0.2), 0.05, 0.45),
            margin: clamp(Number(merged.imageOptions.margin || 2), 0, 10),
            hideBackgroundDots: Boolean(merged.imageOptions.hideBackgroundDots),
        },
    };
};

export const getQrLogoSource = (design) => {
    const normalized = ensureQrDesign(design);

    return normalized.logoPreview || normalized.image || null;
};

export const serializeBuilderData = (data) => {
    if (!data) {
        return data;
    }

    const design = data.design || null;

    if (!design) {
        return data;
    }

    const {
        logoFile,
        logoPreview,
        ...restDesign
    } = design;

    return {
        ...data,
        design: restDesign,
    };
};

const clean = (value) => (typeof value === 'string' ? value.trim() : '');

export const buildQrPreviewData = (builderData) => {
    const type = builderData?.type;
    const content = builderData?.content || {};

    if (!type) {
        return 'https://example.com';
    }

    if (type === 'url') {
        return clean(content.url) || 'https://example.com';
    }

    if (type === 'text') {
        return clean(content.text) || 'QR preview';
    }

    if (type === 'email') {
        const to = clean(content.email);
        const subject = clean(content.subject);
        const body = clean(content.text);
        const params = new URLSearchParams();

        if (subject) params.set('subject', subject);
        if (body) params.set('body', body);

        return `mailto:${to}${params.toString() ? `?${params.toString()}` : ''}`;
    }

    if (type === 'phone') {
        return `tel:${clean(content.phone)}`;
    }

    if (type === 'sms') {
        return `SMSTO:${clean(content.phone)}:${clean(content.text)}`;
    }

    if (type === 'location') {
        const lat = clean(content.lat);
        const lng = clean(content.lng);
        return `geo:${lat},${lng}`;
    }

    if (type === 'wifi') {
        const encryption = clean(content.encryption) || 'WPA';
        const name = clean(content.name);
        const password = clean(content.password);
        return `WIFI:T:${encryption};S:${name};P:${password};;`;
    }

    if (type === 'vcard') {
        const firstName = clean(content.firstName);
        const lastName = clean(content.lastName);
        const phone = clean(content.phone);
        const email = clean(content.email);
        const org = clean(content.org);
        const title = clean(content.title);
        const url = clean(content.url);
        const address = clean(content.address);

        const lines = [
            'BEGIN:VCARD',
            'VERSION:3.0',
            `N:${lastName};${firstName};;;`,
            `FN:${`${firstName} ${lastName}`.trim()}`,
        ];

        if (org) lines.push(`ORG:${org}`);
        if (title) lines.push(`TITLE:${title}`);
        if (phone) lines.push(`TEL;TYPE=CELL:${phone}`);
        if (email) lines.push(`EMAIL:${email}`);
        if (url) lines.push(`URL:${url}`);
        if (address) lines.push(`ADR:;;${address};;;;`);

        lines.push('END:VCARD');

        return lines.join('\n');
    }

    return 'https://example.com';
};